import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { readFolienLinks } from "@/office/powerpoint/links";
import { MAKRO_INTERAKTIV } from "../makros";
import { nichtPruefbar } from "../schnittstelle";

export const lektionLinks: Lesson = {
  id: "links",
  title: "Links und interaktive Schaltflächen",
  summary: "Aus der linearen Folienfolge wird eine Präsentation, durch die man navigieren kann.",
  durationMinutes: 10,
  learningGoals: [
    "Hyperlinks auf Webseiten und auf andere Folien setzen",
    "Interaktive Schaltflächen mit Aktionen versehen",
  ],
  steps: [
    {
      id: "hyperlink",
      title: "Hyperlinks mit sprechendem Text",
      explanation: [
        "Text markieren, Strg + K: Der Link-Dialog ist derselbe wie in Word. Links führen auf " +
          "Webseiten, E-Mail-Adressen oder – über „Aktuelles Dokument“ – auf eine andere " +
          "Folie derselben Präsentation. Während der Präsentation genügt ein Klick.",
        "Der Linktext sagt, wohin es geht: „Anmeldung zum Workshop“, nicht " +
          "„https://…“. Das gilt auf Folien doppelt, weil lange Adressen den knappen Platz " +
          "fressen und niemand sie vom Beamer abschreibt.",
      ],
      keyPoints: [
        "Strg + K – Webseite, E-Mail oder Folie im aktuellen Dokument.",
        "Sprechender Linktext statt Adresse.",
      ],
      task: {
        instruction: "Setze auf einer Folie einen Hyperlink mit sprechendem Text.",
        hint: "Wort markieren, Strg + K, Adresse eintragen – der markierte Text bleibt der Linktext.",
        check: async () => {
          const links = await readFolienLinks();
          if (links === null) {
            return nichtPruefbar("Die Hyperlinks einer Folie", "1.6", "Fahre in der Präsentation mit der Maus über den Link.");
          }
          if (links.length === 0) {
            return fail("In der Präsentation ist noch kein Hyperlink.", "Strg + K öffnet den Link-Dialog.");
          }
          const lesbar = links.filter((link) => link.text !== null);
          const nackt = lesbar.filter((link) => /^(https?:\/\/|www\.)/i.test(link.text ?? ""));
          return fromCriteria(
            [
              criterion(`Hyperlinks in der Präsentation: ${links.length}`, true),
              ...(lesbar.length > 0
                ? [criterion(nackt.length === 0 ? "Kein Link zeigt die nackte Adresse" : `Nackte Adresse: ${nackt[0].text}`, nackt.length === 0)]
                : []),
            ],
            {
              success: "Der Link sitzt – mit einem Text, der sagt, wohin er führt.",
              failure: "Der Linktext zeigt noch die Adresse.",
              hint: "Rechtsklick → Link bearbeiten → „Anzuzeigender Text“.",
            },
          );
        },
      },
    },
    {
      id: "schaltflaechen",
      title: "Interaktive Schaltflächen",
      explanation: [
        "„Einfügen“ → „Formen“ → ganz unten „Interaktive Schaltflächen“: Vor, Zurück, " +
          "Anfang, Ende, Start. Nach dem Aufziehen fragt PowerPoint nach der Aktion – " +
          "„Hyperlink zu: Nächste Folie“ oder zu einer bestimmten Folie.",
        "Jede andere Form und jedes Bild bekommt über „Einfügen“ → „Aktion“ dieselben " +
          "Fähigkeiten. So entsteht etwa eine Agendafolie, deren Punkte zu den Abschnitten " +
          "springen, und auf jeder Trennfolie eine Schaltfläche zurück zur Agenda.",
        "Interaktive Präsentationen passen vor allem dort, wo jemand selbst klickt – am " +
          "Kiosk oder in einer Präsentation zum Durchblättern.",
      ],
      keyPoints: [
        "Einfügen → Formen → Interaktive Schaltflächen.",
        "Einfügen → Aktion macht jede Form zur Schaltfläche.",
        "Agenda mit Sprüngen, Trennfolien mit Rücksprung.",
      ],
      task: {
        instruction: "Füge eine interaktive Schaltfläche ein, die zu einer anderen Folie springt.",
        hint: "Einfügen → Formen → Interaktive Schaltflächen → aufziehen → Aktion „Hyperlink zu“ wählen.",
        macro: MAKRO_INTERAKTIV,
        check: () =>
          pruefeMitMakro(MAKRO_INTERAKTIV, (bericht) =>
            fromCriteria(
              [
                criterion(`Interaktive Schaltflächen: ${bericht.zahl("schaltflaechen") ?? 0}`, (bericht.zahl("schaltflaechen") ?? 0) > 0),
                criterion(`Formen mit Aktion: ${bericht.zahl("aktionen") ?? 0}`, (bericht.zahl("aktionen") ?? 0) > 0),
              ],
              {
                success: "Die Präsentation lässt sich jetzt per Klick durchnavigieren.",
                failure: "Es gibt noch keine Schaltfläche mit Aktion.",
              },
            ),
          ),
      },
    },
  ],
};
